import { AxiosInstance, AxiosResponse } from 'axios';
import { ICache, CK_BILL_TO_ID, CK_CUSTOMER_ACCOUNT, CK_USER_ID, CK_USER_TOKEN } from './cache';
import { isNil } from 'lodash-es';

interface ITMSBillingInfo {
    location_name: string;
    location_id: string;
    location_code: string;
    location_phone: string;
    location_fax?: null;
    location_street?: null;
    location_street2?: null;
    location_city: string;
    location_state: string;
    location_zip: string;
    location_payment_type: string;
    request_credit_limit: string;
    request_netterm: string;
    min_pallet: string;
    max_pallet: string;
}

interface ITMSBillingInfoListEntity {
    location_name: string;
    location_code: string;
    location_id: number;
    location_phone: string;
    location_fax?: null;
    location_street?: string | null;
    location_street2?: null;
    location_city?: string | null;
    location_state?: string | null;
    location_zip?: string | null;
    min_pallet: string;
    max_pallet: string;
    location_payment_type: string;
}

interface Shipping {
    location_name: string;
    location_id: string;
    location_phone: string;
    location_fax?: null;
    location_street?: null;
    location_street2?: null;
    location_city: string;
    location_state: string;
    location_zip: string;
}

interface ITMSUser {
    analytics_user_id?: string;
    user_id: string;
    user_username: string;
    user_email: string;
    user_firstname: string;
    user_lastname: string;
    user_employee_code?: null;
    use_sso_login: string;
    use_sso_domain: string;
    user_dob: string;
    user_dob_year: string;
    user_dob_month: string;
    user_dob_day: string;
    user_mobile?: null;
    user_zip?: null;
    user_work?: null;
    user_education?: null;
    user_tos: string;
    user_source?: null;
    user_firstlast: string;
    file_id?: null;
    file_medium_url?: null;
    file_thumbnail_url?: null;
    company_id: string;
    group_customer: string;
    user_password_expire: string;
    user_groups: number;
    is_ad_user: number;
    password_expire: number;
    schedule_pickup_access: string;
    is_vendor?: number;
    is_claims_access?: number;
    is_sb_cust?: number;
}

interface ITMSCustomerAccount {
    billing: ITMSBillingInfo | ITMSBillingInfoListEntity;
    billing_list?: (ITMSBillingInfoListEntity)[] | null;
    shipping: Shipping;
    user: ITMSUser;
}

interface IChangeBillToResponse {
    status: number;
    status_code: string;
    data: {
        status: number;
        message: string;
        data: {
            min_pallet: number;
            max_pallet: number;
        };
    };
}

interface Dictionary {
    [key: string]: any;
}

enum TMSCustomerAccountServiceEvent {
    BillToChanged = 'billToChanged',
}

class TMSCustomerAccountService {
    private dependence: {
        cache: ICache;
        tmsService: AxiosInstance;
        tmsTransformRequestService: AxiosInstance;
    };

    static create(dependence: {
        cache: ICache;
        tmsService: AxiosInstance;
        tmsTransformRequestService: AxiosInstance;
    }) {
        return new TMSCustomerAccountService(dependence);
    }

    constructor(dependence: {
        cache: ICache;
        tmsService: AxiosInstance;
        tmsTransformRequestService: AxiosInstance;
    }) {
        this.dependence = dependence;
    }

    get isGuest() {
        return isNil(localStorage.getItem('UserID'));
    }

    get billToList() {
        const { cache } = this.dependence;
        const customerAccount = cache.getItem<AxiosResponse<ITMSCustomerAccount>>(
            CK_CUSTOMER_ACCOUNT
        );
        if (!customerAccount) {
            return [];
        }

        return customerAccount.data.billing_list || [];
    }

    get currentBillTo() {
        const { cache } = this.dependence;
        const customerAccount = cache.getItem<AxiosResponse<ITMSCustomerAccount>>(
            CK_CUSTOMER_ACCOUNT
        );
        if (!customerAccount) {
            return null;
        }

        return customerAccount.data.billing;
    }

    findBillTo(comparator: (item: ITMSBillingInfoListEntity) => boolean) {
        let found: ITMSBillingInfoListEntity | null = null;
        this.eachBillTo(i => {
            if (comparator(i)) {
                found = i;
            }
        });
        return found;
    }

    eachBillTo(func: (item: ITMSBillingInfoListEntity, index: number) => void) {
        this.billToList.forEach(func);
    }
    async getWriteNewWiseRefreshLogin() {
        const { cache, tmsTransformRequestService } = this.dependence;
        const response = await tmsTransformRequestService.get(
            `/write_new/write_new_wise_refresh_login.php`
        );
        return response.data;
    }
    async fetchThenCacheCustomerAccount(args: Record<string, any> = {}) {
        const { cache, tmsTransformRequestService } = this.dependence;
        const response = await tmsTransformRequestService.post<ITMSCustomerAccount>(
            `/write_new/get_customer_account.php`,
            args
        );

        if (response.request.response === '-1') {
            throw new Error('No locations found');
        }

        if (response.data?.billing_list) {
            for (let i = 0; i < response.data.billing_list.length; i++) {
                const item: ITMSBillingInfoListEntity = response.data.billing_list[i];
                if (item.location_name === response.data.billing.location_name) {
                    response.data.billing.location_code = item.location_code;
                    console.log('替换数据：', item.location_name, item.location_code);
                }
            }
        }

        // 如果存在user数据
        if (response.data?.user) {
            // 保存 is_vendor 到 localStorage
            if (response.data.user.is_vendor !== undefined) {
                localStorage.setItem('IsVendor', response.data.user.is_vendor ? '1' : '0');
            }

            // 统一存储 claims access 的键名和值
            if (response.data.user.is_claims_access !== undefined) {
                const isClaimsAccess = response.data.user.is_claims_access;
                localStorage.setItem('ClaimsAccess', isClaimsAccess ? '1' : '0');
                localStorage.setItem('isClaimsAccess', isClaimsAccess ? 'true' : 'false');
            }

            // 对于 isSBCust，我们不能在这里判断，因为没有 group 信息
            // 这个值应该在 fetchUserRoles 中设置
        }

        cache.removeItem(CK_CUSTOMER_ACCOUNT);
        cache.setItem(CK_CUSTOMER_ACCOUNT, response);
        cache.setItem(CK_BILL_TO_ID, response.data.billing.location_id);

        return response.data;
    }

    async checkHasBillto(args: Record<string, any> = {}) {
        const { cache, tmsTransformRequestService } = this.dependence;
        const response = await tmsTransformRequestService.post<any>(
            `/write_new/get_customer_account.php`,
            args
        );

        if (response.data != '-1') {
            response.data?.billing_list?.forEach((item: ITMSBillingInfoListEntity) => {
                if (item.location_name === response.data.billing.location_name) {
                    response.data.billing.location_code = item.location_code;
                    console.log('替换数据：', item.location_name, item.location_code);
                }
            });
            cache.setItem(CK_CUSTOMER_ACCOUNT, response);
            return true;
        } else {
            return false;
        }
    }

    async useBillTo(nextBilling: ITMSBillingInfoListEntity) {
        const { cache, tmsService } = this.dependence;

        const customerAccount = cache.getItem<AxiosResponse<ITMSCustomerAccount>>(
            CK_CUSTOMER_ACCOUNT
        );
        if (!customerAccount) {
            throw new Error('CustomerAccount not found in local storage!');
        }

        const { data, ...remains } = customerAccount;
        const { billing, ...others } = data;

        // 在BNP系统中，location_name是unique的
        if (
            nextBilling.location_name === billing.location_name ||
            nextBilling.location_id === billing.location_id
        ) {
            return true;
        }

        const billToId = nextBilling.location_id;
        const params: Dictionary = {
            UserID: cache.getItem<string>(CK_USER_ID),
            UserToken: cache.getItem<string>(CK_USER_TOKEN),
            billto_id: billToId,
        };
        const response = await tmsService.post<IChangeBillToResponse>(
            `/apinew/billing/switchUserBillto`,
            params
        );

        if (response.data.status_code === 'success') {
            cache.setItem<AxiosResponse<ITMSCustomerAccount>>(CK_CUSTOMER_ACCOUNT, {
                data: {
                    billing: nextBilling,
                    ...others,
                },
                ...remains,
            });
            cache.setItem(CK_BILL_TO_ID, billToId);

            window.dispatchEvent(
                new CustomEvent(TMSCustomerAccountServiceEvent.BillToChanged, {
                    detail: nextBilling,
                })
            );
        }

        return response.data;
    }
}

export {
    TMSCustomerAccountService,
    TMSCustomerAccountServiceEvent,
};
export type {
    ICache,
    ITMSBillingInfo,
    ITMSCustomerAccount,
};



// WEBPACK FOOTER //
// ./node_modules/tslint-loader??ref--1!./src/services/user.ts